import { FormInputVariants } from '@teko/ui';
import { Formik, FormikProps } from 'formik';
import { Button, ButtonProps } from '@teko/ds-desktop';
import React, {
  ForwardedRef,
  ReactElement,
  forwardRef,
  ReactNode,
} from 'react';
import {
  ButtonGroup,
  Status,
  StyledBottomNavBar,
  StyledFormInput,
} from './styles';
import { InputField } from './types';
import { ObjectMap } from 'types';

interface GeneralFormikFormProps<
  T extends ObjectMap,
  P = Partial<Record<keyof T, boolean>>,
  K = Partial<Record<keyof T, string>>,
> {
  isMobile?: boolean;
  initialFormValues: T;
  requiredFields: P;
  feedbacks: K;
  messages: { success: boolean; content: string }[];
  validate: (payload: Partial<T>) => K;
  submit: (payload: Partial<T>) => Promise<T | undefined>;
  getInputFields: (
    values: Partial<T>,
    setFieldValue: FormikProps<Partial<T>>['setFieldValue'],
  ) => InputField<T>[];
  getButtonsProps: (
    values: Partial<T>,
    errors: FormikProps<Partial<T>>['errors'],
    dirty?: boolean,
  ) => ButtonProps[];
  policies?: ReactNode;
  additionalButtons?: ReactNode;
}

const RenderGeneralFormikForm = <T extends ObjectMap>(
  props: GeneralFormikFormProps<T>,
  ref: ForwardedRef<FormikProps<Partial<T>>>,
) => {
  const {
    isMobile = false,
    getInputFields,
    initialFormValues,
    requiredFields,
    feedbacks,
    messages,
    validate,
    submit,
    getButtonsProps,
    additionalButtons,
    policies,
    ...rest
  } = props;

  const handleEmptyField = (
    event: React.FocusEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: string | number) => void,
  ) => {
    // reset to value before editing
    const savedValue = initialFormValues?.[event.target.name];
    if (!event.target.value && savedValue && typeof savedValue !== 'boolean') {
      setFieldValue(event.target.name, savedValue);
    }
  };
  const handleSubmit = (
    values: Partial<T>,
    actions: {
      resetForm: ({
        values,
        touched,
      }: {
        values: Partial<T>;
        touched: ObjectMap;
      }) => void;
    },
  ) => {
    submit(values).then(() => {
      actions.resetForm({
        values,
        touched: {},
      });
    });
  };

  const renderForm = ({
    values,
    dirty,
    errors,
    touched,
    setFieldTouched,
    handleSubmit,
    handleChange,
    setFieldValue,
  }: FormikProps<Partial<T>>): React.ReactNode => {
    const buttonProps = getButtonsProps(values, errors, dirty);
    const inputFields = getInputFields(values, setFieldValue);

    const renderButtons =
      buttonProps.length > 0
        ? buttonProps.map((prop, index) => <Button key={index} {...prop} />)
        : null;

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      handleChange(e);
      setFieldTouched(name, initialFormValues[name] !== e.target.value, false);
    };
    return (
      <>
        <form onSubmit={handleSubmit} {...rest}>
          {messages
            .filter((message) => !!message.content)
            .map((message, idx) => (
              <Status key={idx} success={message.success}>
                {message.content}
              </Status>
            ))}
          {inputFields.map(({ condition = true, name, ...rest }) =>
            condition ? (
              <StyledFormInput
                key={name as string}
                name={name as string}
                value={(values?.[name] as string) || ''}
                onChange={onChange}
                onBlur={(event) => {
                  handleEmptyField(event, setFieldValue);
                }}
                variant={
                  touched[name] && feedbacks[name]
                    ? FormInputVariants.invalid
                    : undefined
                }
                feedback={
                  touched[name] && feedbacks[name]
                    ? String(feedbacks[name])
                    : undefined
                }
                required={requiredFields[name as keyof typeof requiredFields]}
                {...rest}
              />
            ) : null,
          )}
          {additionalButtons ? additionalButtons : null}
          {policies ? policies : null}
          {renderButtons &&
            (!isMobile ? (
              <ButtonGroup>{renderButtons}</ButtonGroup>
            ) : (
              <StyledBottomNavBar zIndex={99}>
                <ButtonGroup>{renderButtons}</ButtonGroup>
              </StyledBottomNavBar>
            ))}
        </form>
      </>
    );
  };

  return (
    <Formik
      innerRef={ref}
      initialValues={initialFormValues}
      onSubmit={handleSubmit}
      validate={validate}
    >
      {renderForm}
    </Formik>
  );
};

export const GeneralFormikForm = forwardRef(RenderGeneralFormikForm) as <
  T extends ObjectMap,
>(
  p: GeneralFormikFormProps<T> & { ref?: ForwardedRef<FormikProps<T>> },
) => ReactElement;
